import { Component, type ErrorInfo, type ReactNode } from 'react';
import type { Logger } from '@puzzlemania/shared';

export interface ErrorBoundaryProps {
  logger: Logger;
  fallback: ReactNode | ((reset: () => void) => ReactNode);
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches synchronous render-phase errors in its subtree. Always logs before
 * rendering the fallback — never swallows silently. Does not catch errors from
 * event handlers, timers, or promise rejections; see globalHandlers.ts for those.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.logger.error(`Unhandled render error: ${info.componentStack ?? ''}`, error);
  }

  private _reset = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback(this._reset)
        : this.props.fallback;
    }
    return this.props.children;
  }
}
